import { type Mark as ProseMirrorMark, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { renderToReactElement } from '@tiptap/static-renderer';
import { memo, type ReactNode, useMemo } from 'react';

import { type ShortIdProviderProps } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { parseSeedablePresentationTextBlockBody } from '~/components/v2/components/showcase/parsePresentationTextBlockBody';
import { PRESENTATION_TEXT_BLOCK_EXTENSIONS } from '~/components/v2/components/showcase/presentationTextBlockExtensions';
import {
  PRESENTATION_TEXT_BLOCK_BULLET_LIST_CLASS,
  PRESENTATION_TEXT_BLOCK_CONTAINER_CLASS,
  PRESENTATION_TEXT_BLOCK_HEADING_CLASS_BY_LEVEL,
  PRESENTATION_TEXT_BLOCK_HEADING_FONT_CLASS,
  PRESENTATION_TEXT_BLOCK_INSET_CLASS,
  PRESENTATION_TEXT_BLOCK_LINK_CLASS,
  PRESENTATION_TEXT_BLOCK_LIST_ITEM_CLASS,
  PRESENTATION_TEXT_BLOCK_ORDERED_LIST_CLASS,
  PRESENTATION_TEXT_BLOCK_PARAGRAPH_CLASS,
  PRESENTATION_TEXT_BLOCK_TEXT_ALIGN_CLASS,
  PRESENTATION_TEXT_BLOCK_VIEWER_RHYTHM_CLASS,
} from '~/components/v2/components/showcase/presentationTextBlockTypography';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';

type PresentationContentBlock = NonNullable<ShortIdProviderProps['presentationContent']>['blocks'][number];

/** Shared by every node-mapping component below — the static renderer's node render contract. */
export type PublicShowcaseTextBlockNodeProps = {
  children?: ReactNode;
  node: ProseMirrorNode;
};

/** Shared by every mark-mapping component below — the static renderer's mark render contract. */
export type PublicShowcaseTextBlockMarkProps = {
  children?: ReactNode;
  mark: ProseMirrorMark;
};

const HEADING_TAG_BY_LEVEL: Record<number, 'h1' | 'h2' | 'h3'> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
};

/**
 * The static-render markMapping already restricts links to the extension contract, but the doc is
 * author-controlled wire data — this render-time gate guarantees nothing outside absolute http(s)
 * ever reaches an href, whatever the doc claims.
 */
const isSafeTextBlockHref = (href: unknown): href is string => typeof href === 'string' && /^https?:\/\//i.test(href);

const PublicShowcaseTextBlockHeading = memo(({ children, node }: PublicShowcaseTextBlockNodeProps) => {
  const { headlineColor } = usePublicCustomColors();

  const HeadingTag = HEADING_TAG_BY_LEVEL[node.attrs.level] ?? 'h2';

  return (
    <HeadingTag
      className={cn(
        PRESENTATION_TEXT_BLOCK_HEADING_FONT_CLASS,
        PRESENTATION_TEXT_BLOCK_HEADING_CLASS_BY_LEVEL[node.attrs.level],
        PRESENTATION_TEXT_BLOCK_TEXT_ALIGN_CLASS[node.attrs.textAlign],
      )}
      // The headline color is publisher-configured at runtime — no class can express it.
      style={headlineColor ? { color: headlineColor } : undefined}
    >
      {children}
    </HeadingTag>
  );
});

PublicShowcaseTextBlockHeading.displayName = 'PublicShowcaseTextBlockHeading';

const PublicShowcaseTextBlockParagraph = memo(({ children, node }: PublicShowcaseTextBlockNodeProps) => {
  return (
    <p
      className={cn(
        PRESENTATION_TEXT_BLOCK_PARAGRAPH_CLASS,
        PRESENTATION_TEXT_BLOCK_TEXT_ALIGN_CLASS[node.attrs.textAlign],
      )}
    >
      {children}
    </p>
  );
});

PublicShowcaseTextBlockParagraph.displayName = 'PublicShowcaseTextBlockParagraph';

const PublicShowcaseTextBlockBulletList = memo(({ children }: PublicShowcaseTextBlockNodeProps) => {
  return <ul className={PRESENTATION_TEXT_BLOCK_BULLET_LIST_CLASS}>{children}</ul>;
});

PublicShowcaseTextBlockBulletList.displayName = 'PublicShowcaseTextBlockBulletList';

const PublicShowcaseTextBlockOrderedList = memo(({ children }: PublicShowcaseTextBlockNodeProps) => {
  return <ol className={PRESENTATION_TEXT_BLOCK_ORDERED_LIST_CLASS}>{children}</ol>;
});

PublicShowcaseTextBlockOrderedList.displayName = 'PublicShowcaseTextBlockOrderedList';

const PublicShowcaseTextBlockListItem = memo(({ children }: PublicShowcaseTextBlockNodeProps) => {
  return <li className={PRESENTATION_TEXT_BLOCK_LIST_ITEM_CLASS}>{children}</li>;
});

PublicShowcaseTextBlockListItem.displayName = 'PublicShowcaseTextBlockListItem';

const PublicShowcaseTextBlockLink = memo(({ children, mark }: PublicShowcaseTextBlockMarkProps) => {
  const { areExternalLinksClickable } = useExternalLinksClickableContext();
  const href = mark.attrs.href;

  // Out-of-allowlist hrefs render their text unlinked rather than dropping content.
  if (!areExternalLinksClickable || !isSafeTextBlockHref(href)) {
    return <>{children}</>;
  }

  return (
    <a className={PRESENTATION_TEXT_BLOCK_LINK_CLASS} href={href} rel="noopener noreferrer nofollow" target="_blank">
      {children}
    </a>
  );
});

PublicShowcaseTextBlockLink.displayName = 'PublicShowcaseTextBlockLink';

export type PublicShowcaseTextBlockUnhandledContentProps = {
  children?: ReactNode;
};

// Fallback for node/mark types the renderer has no handler for (an extension without renderHTML):
// keep the children, skip the unknown wrapper, never throw on a public page.
const PublicShowcaseTextBlockUnhandledContent = memo(({ children }: PublicShowcaseTextBlockUnhandledContentProps) => {
  return <>{children}</>;
});

PublicShowcaseTextBlockUnhandledContent.displayName = 'PublicShowcaseTextBlockUnhandledContent';

export type PublicShowcaseTextBlockProps = {
  block: Pick<PresentationContentBlock, 'body' | 'inset'>;
};

export const PublicShowcaseTextBlock = memo(({ block }: PublicShowcaseTextBlockProps) => {
  const content = useMemo(() => {
    const textBlockDocument = parseSeedablePresentationTextBlockBody(block.body);

    if (!textBlockDocument) {
      return null;
    }

    try {
      return renderToReactElement({
        content: textBlockDocument,
        extensions: PRESENTATION_TEXT_BLOCK_EXTENSIONS,
        options: {
          nodeMapping: {
            bulletList: PublicShowcaseTextBlockBulletList,
            heading: PublicShowcaseTextBlockHeading,
            listItem: PublicShowcaseTextBlockListItem,
            orderedList: PublicShowcaseTextBlockOrderedList,
            paragraph: PublicShowcaseTextBlockParagraph,
          },
          markMapping: {
            link: PublicShowcaseTextBlockLink,
          },
          unhandledMark: PublicShowcaseTextBlockUnhandledContent,
          unhandledNode: PublicShowcaseTextBlockUnhandledContent,
        },
      });
    } catch {
      // Schema-valid docs can still fail in the renderer; a malformed body must never crash
      // the public page, so the block renders nothing.
      return null;
    }
  }, [block.body]);

  if (!content) {
    return null;
  }

  return (
    // p-4 matches the collection column's header/gallery gutter (16px); text-[15px] is the proto's
    // body size (between text-14 and text-16); the rhythm class is the proto's 10px spacing
    // between top-level doc nodes.
    <div
      className={cn(
        PRESENTATION_TEXT_BLOCK_CONTAINER_CLASS,
        PRESENTATION_TEXT_BLOCK_VIEWER_RHYTHM_CLASS,
        block.inset && PRESENTATION_TEXT_BLOCK_INSET_CLASS,
      )}
      data-testid="PUBLIC_COLLECTION_TEXT_BLOCK"
    >
      {content}
    </div>
  );
});

PublicShowcaseTextBlock.displayName = 'PublicShowcaseTextBlock';
